import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import Typography from "@mui/joy/Typography";
import Link from "@mui/joy/Link";
import { theme } from "../../styles/theme";
import { useQuery } from "@tanstack/react-query";
import { paymentApi } from "../../api/paymentApi";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import useMixpanelPromotion from "../../hooks/useMixpanelPromotion";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import { useRecoilValue } from "recoil";
import { PATH } from "../../routes/paths";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import mixpanel from "mixpanel-browser";

export default function TossPaymentsPromotionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");
  const payAmount = Number(searchParams.get("amount"));

  useAutoLoginPromotion();
  const user = useRecoilValue(userSimpleAtom);

  const trackPromotionPurchaseSuccess = useMixpanelPromotion({
    eventName: "결제 완료",
  });

  const { isLoading, isSuccess, isError } = useQuery({
    queryKey: ["tossPromoConfirm", orderId, paymentKey, payAmount],
    enabled: Boolean(
      orderId &&
        paymentKey &&
        payAmount &&
        user?.trackId &&
        user?.userId &&
        user?.email
    ),
    queryFn: async () => {
      return await paymentApi.confirmTossPaymentsPromotion({
        orderId: orderId as string,
        paymentKey: paymentKey as string,
        payAmount: payAmount as number,
      });
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (responseData) => {
      if (
        responseData?.result?.isConfirmed !== true &&
        responseData?.result?.failureMessage
      ) {
        alert(responseData?.result?.failureMessage);
        return;
      }

      // try {
      //   // 결제 성공 후 재진입 시 결제 페이지의 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송을 1회 차단
      //   localStorage.setItem("suppress_payment_page_view", "1");
      // } catch (e) {}

      // user 정보는 enabled에서 보장됨
      // Mixpanel identify 먼저 호출
      const promotionInstance = (mixpanel as any).promotion;
      if (promotionInstance && user?.trackId) {
        promotionInstance.identify(user.trackId);
      }

      // 이후 이벤트 전송
      trackCapiStandardEvent("Purchase", {
        value: 395000,
        currency: "KRW",
        externalId: user?.userId,
        email: user?.email,
      }, {
        eventId: orderId ?? undefined
      });

      trackPromotionPurchaseSuccess();
    },
    onError: () => {
      alert("결제가 실패했어요. 다시 시도해 주세요.");
    },
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(PATH.promotionCompleted, { replace: true });
    }
  }, [isSuccess, navigate]);

  if (!orderId || !paymentKey || !payAmount) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>{"만료된 페이지"}</Typography>
          </Stack>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>
              {"결제 처리 중 오류가 발생했습니다."}
            </Typography>
            <Typography variant={"H3"}>
              <Link
                href="mailto:support@orwellhealth.org"
                sx={{
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                support@orwellhealth.org
              </Link>
              {"로 문의해 주세요."}
            </Typography>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
